// client/src/hooks/useLinkInterception.js
import { useEffect } from 'react';

/**
 * Restores "open in a new tab" inside the installed PWA (ST-00088).
 *
 * Standalone PWAs have no tab strip, so the browser turns a middle click into a
 * whole new OS window. One delegated listener catches those gestures on any
 * internal link and routes them into the in-app tab strip instead:
 *
 *   middle click / ctrl+click  -> background tab in this window
 *   ctrl+shift+click           -> left alone, so a real new window still opens
 *
 * @param openTab  (url, { background }) => tabId
 */
export default function useLinkInterception(openTab) {
  useEffect(() => {
    function onAuxClick(event) {
      if (event.button !== 1 || event.defaultPrevented) return;
      const url = internalHref(event.target);
      if (!url) return;
      event.preventDefault();
      openTab(url, { background: true });
    }

    function onMouseDown(event) {
      // Suppress the middle-click autoscroll cursor (Windows) / paste (X11)
      // before it starts. auxclick fires too late to prevent it.
      if (event.button !== 1) return;
      if (internalHref(event.target)) event.preventDefault();
    }

    function onClick(event) {
      if (event.button !== 0 || event.defaultPrevented) return;
      // Shift is the deliberate escape hatch to a real window — leave it.
      if (event.shiftKey || !(event.ctrlKey || event.metaKey)) return;
      const url = internalHref(event.target);
      if (!url) return;
      event.preventDefault();
      openTab(url, { background: true });
    }

    document.addEventListener('auxclick', onAuxClick);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('auxclick', onAuxClick);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('click', onClick);
    };
  }, [openTab]);
}

/**
 * The in-app path this event targets, or null if it isn't ours to handle —
 * external hosts, downloads, explicit targets, anchors and API URLs all stay
 * with the browser.
 */
function internalHref(target) {
  const anchor = target instanceof Element ? target.closest('a[href]') : null;
  if (!anchor) return null;
  if (anchor.hasAttribute('download')) return null;
  if (anchor.target && anchor.target !== '_self') return null;

  const raw = anchor.getAttribute('href');
  if (!raw || raw.startsWith('#')) return null;

  let url;
  try {
    url = new URL(anchor.href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  // Server-rendered endpoints, not client routes.
  if (url.pathname.startsWith('/api/') || url.pathname.startsWith('/odata')) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}
